import type { TOrgInfo } from "@/types";
import Icon from "@components/ui/icon";
import Input from "@components/ui/input";
import Popover from "@components/ui/popover";
import { ScrollArea } from "@components/ui/scroll-area";
import { cn } from "@lib/cn";
import { useState } from "react";
import { useCurrentAccount } from "../providers/current-account-provider";

const STAGING_B2B_ACCOUNT_ID = "0ee12c707619bea9945ae65b857e23c7";
const PROD_B2B_ACCOUNT_ID = "7e8cb68e27e2522b115e9d50add21d78";

const AccountsSearch = () => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    setSearchValue,
    setIsLoading,
    setAccount,
    searchValue,
    handleSearchAccounts,
    isLoading,
    account,
    accounts,
    isUninitialized,
    currentAccount,
  } = useCurrentAccount();

  const isB2B =
    currentAccount?._id === STAGING_B2B_ACCOUNT_ID ||
    currentAccount?._id === PROD_B2B_ACCOUNT_ID;
  if (!isB2B) return null;

  return (
    <>
      <div className="tw-mb-6 tw-flex tw-items-center tw-justify-between">
        <span className="tw-block tw-font-sans tw-text-[13px]/normal tw-font-semibold tw-text-blue-base">
          Current account
        </span>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <Popover.Trigger className="tw-w-60">
            <span className="tw-flex tw-h-9 tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-rounded tw-border tw-border-solid tw-border-blue-100 tw-bg-white tw-px-3 tw-py-1 tw-pr-1 tw-font-sans tw-text-xs tw-font-normal tw-text-blue-base">
              <span className="tw-block tw-w-[93%] tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
                {account?.title}
              </span>
              <Icon
                icon="ri-arrow-down-s-fill"
                className={cn("tw-mr-1 tw-text-xl/none", {
                  "tw-rotate-180 tw-transition-transform": isOpen,
                })}
              />
            </span>
          </Popover.Trigger>
          <Popover.Content className="tw-z-[99999] tw-w-60 tw-rounded-lg tw-border tw-border-solid tw-border-blue-100 tw-bg-white tw-text-xs tw-shadow-sm">
            <div className="tw-px-2 tw-pt-2">
              <Input
                size="sm"
                placeholder="Search"
                value={searchValue}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchValue(value);
                  if (!value) return;
                  setIsLoading(true);
                  handleSearchAccounts(value);
                }}
              />
            </div>
            <ScrollArea maxHeight={300}>
              <div className="tw-space-y-1 tw-p-2 empty:tw-pt-0">
                {accounts?.map((account: TOrgInfo) => (
                  <AccountOption
                    key={account._id}
                    account={account}
                    setAccount={() => {
                      setAccount(account);
                      setIsOpen(false);
                    }}
                  />
                ))}
                {isLoading &&
                  Array.from({ length: 5 }).map((_, index) => (
                    <AccountOptionSkeleton key={index} />
                  ))}
                {!isLoading && !isUninitialized && !accounts?.length && (
                  <AccountsEmptyState />
                )}
              </div>
            </ScrollArea>
          </Popover.Content>
        </Popover>
      </div>
      <hr className="tw-my-6 tw-border-t tw-border-blue-100" />
    </>
  );
};

export default AccountsSearch;

const AccountOption = ({
  account,
  setAccount,
}: {
  account: TOrgInfo;
  setAccount: (account: TOrgInfo) => void;
}) => {
  return (
    <div
      className="tw-flex tw-h-9 tw-w-[222px] tw-cursor-pointer tw-flex-col tw-justify-center tw-rounded tw-px-3 tw-transition-colors hover:tw-bg-blue-50"
      onClick={() => setAccount(account)}
    >
      <span className="block tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
        {account.title}
      </span>
      <span className="block tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-[8px]/none">
        {account._id}
      </span>
    </div>
  );
};

const AccountOptionSkeleton = () => {
  return (
    <div className="tw-flex tw-h-9 tw-w-full tw-flex-col tw-justify-center tw-rounded tw-px-3 tw-opacity-70">
      <span className="block tw-h-2 tw-w-full tw-animate-skeleton-loading tw-rounded-2xl tw-bg-skeleton-gradient"></span>
      <span className="block tw-mt-1 tw-h-1 tw-w-1/2 tw-animate-skeleton-loading tw-rounded-2xl tw-bg-skeleton-gradient"></span>
    </div>
  );
};

const AccountsEmptyState = () => {
  return (
    <div className="tw-flex tw-h-9 tw-w-full tw-cursor-not-allowed tw-flex-col tw-justify-center tw-rounded tw-px-3">
      No options found
    </div>
  );
};
